import * as THREE from 'three'
import Experience from "../../Experience";

export default class Planet
{
  constructor()
  {
    this.experience = new Experience()
    this.scene = this.experience.scene
    this.resources = this.experience.resources
    this.debug = this.experience.debug

    // 初期テクスチャ生成
    let currentScale = 18.0;
    let currentDetail = 8;
    let currentContrast = 2.0;
    let { colorTexture, bumpTexture } = this.generateMoonTextures(512, currentScale, currentDetail, currentContrast);

    // Blenderの設定を正確に再現したマテリアル
    this.moonMaterial = new THREE.MeshStandardMaterial({
        map: colorTexture,      // ノイズ+カラーランプ適用済み
        bumpMap: bumpTexture,   // 高解像度バンプマップ
        bumpScale: 0.05,        // Blenderの0.5に相当（Three.jsでは小さめの値）
        roughness: 0.98,        // 非常に粗い
        metalness: 0.0,         // 非金属
    });

    // 月の球体作成（Blenderと同じスケール）
    this.moonGeometry = new THREE.SphereGeometry(3, 16, 16); // 高解像度
    this.moon = new THREE.Mesh(this.moonGeometry, this.moonMaterial);
    // this.moon.position.set(0, 0, 0);
    this.moon.position.set(-4, -4, 16);
    this.scene.add(this.moon);

    if (this.debug.active) {
      this.debugFolder = this.debug.ui.addFolder('Planet');
      this.debugFolder.add(this.moon.position, 'x').name('Moon Position X').min(-10).max(10).step(0.01);
      this.debugFolder.add(this.moon.position, 'y').name('Moon Position Y').min(-10).max(10).step(0.01);
      this.debugFolder.add(this.moon.position, 'z').name('Moon Position Z').min(-10).max(30).step(0.01);
      this.debugFolder.close();
    }
  }

  // FBM（Fractal Brownian Motion）ノイズ関数
  fbmNoise(x, y, octaves = 8, persistence = 0.5, scale = 18.0) {
      let value = 0;
      let amplitude = 1;
      let frequency = scale / 512.0;
      let maxValue = 0;

      for (let i = 0; i < octaves; i++) {
          // 複数の周波数のサイン波を組み合わせてFBMノイズを近似
          value += amplitude * (
              Math.sin(x * frequency * Math.PI * 2.0) * Math.cos(y * frequency * Math.PI * 2.0) * 0.5 +
              Math.sin(x * frequency * Math.PI * 3.1) * Math.cos(y * frequency * Math.PI * 2.7) * 0.3 +
              Math.sin(x * frequency * Math.PI * 1.7) * Math.cos(y * frequency * Math.PI * 1.9) * 0.2
          );
          maxValue += amplitude;
          amplitude *= persistence;
          frequency *= 2.0;
      }
      return value / maxValue;
  }

  // Blenderのカラーランプを再現（0.2から0.4のグレー）
  applyColorRamp(noiseValue, contrast = 2.0) {
    // ノイズ値を-1〜1から0〜1に変換
    let normalizedValue = (noiseValue + 1.0) * 0.5;

    // コントラストを適用
    normalizedValue = Math.pow(normalizedValue, contrast);

    // Blenderのカラーランプ: 0.2（黒っぽい）から0.4（濃いグレー）
    const minColor = 0.2;
    const maxColor = 0.4;
    const finalValue = minColor + (maxColor - minColor) * normalizedValue;

    return Math.max(0, Math.min(1, finalValue));
  }

  // ノイズテクスチャとバンプマップ生成
  generateMoonTextures(size = 512, scale = 18.0, detail = 8, contrast = 2.0) {
    // カラーテクスチャ用キャンバス
    const colorCanvas = document.createElement('canvas');
    colorCanvas.width = size;
    colorCanvas.height = size;
    const colorCtx = colorCanvas.getContext('2d');
    const colorImageData = colorCtx.createImageData(size, size);

    // バンプマップ用キャンバス
    const bumpCanvas = document.createElement('canvas');
    bumpCanvas.width = size;
    bumpCanvas.height = size;
    const bumpCtx = bumpCanvas.getContext('2d');
    const bumpImageData = bumpCtx.createImageData(size, size);

    for (let i = 0; i < colorImageData.data.length; i += 4) {
        const x = (i / 4) % size;
        const y = Math.floor((i / 4) / size);

        // FBMノイズ生成
        const noiseValue = this.fbmNoise(x, y, detail, 0.9, scale);

        // カラーランプを適用
        const colorValue = this.applyColorRamp(noiseValue, contrast);
        const colorByte = Math.floor(colorValue * 255);

        // カラーテクスチャ
        colorImageData.data[i] = colorByte;     // R
        colorImageData.data[i + 1] = colorByte; // G
        colorImageData.data[i + 2] = colorByte; // B
        colorImageData.data[i + 3] = 255;       // A

        // バンプマップ（ノイズ値を直接使用）
        const bumpValue = Math.floor((noiseValue + 1.0) * 127.5);
        bumpImageData.data[i] = bumpValue;     // R
        bumpImageData.data[i + 1] = bumpValue; // G
        bumpImageData.data[i + 2] = bumpValue; // B
        bumpImageData.data[i + 3] = 255;       // A
    }

    colorCtx.putImageData(colorImageData, 0, 0);
    bumpCtx.putImageData(bumpImageData, 0, 0);

    const colorTexture = new THREE.CanvasTexture(colorCanvas);
    const bumpTexture = new THREE.CanvasTexture(bumpCanvas);

    colorTexture.wrapS = THREE.RepeatWrapping;
    colorTexture.wrapT = THREE.RepeatWrapping;
    bumpTexture.wrapS = THREE.RepeatWrapping;
    bumpTexture.wrapT = THREE.RepeatWrapping;

    return { colorTexture, bumpTexture };
  }
}